/**
 * <s-gallery-thumbs> — Thumbnail navigation for a scroll-snap product gallery.
 *
 * Required child markup:
 *   [data-product-gallery]  — Horizontal scroll-snap track; each direct child is one full-width slide.
 *   button[data-thumb-index][commandfor=<this id>][command="--show-slide"] — Thumbnail buttons.
 *
 * The thumbnail for the slide in view gets aria-current="true".
 */
class GalleryThumbs extends HTMLElement {
  #track = null;
  #thumbs = [];
  #observer = null;
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.#track = this.querySelector('[data-product-gallery]');
    this.#thumbs = Array.from(this.querySelectorAll('[data-thumb-index]'));
    if (!this.#track || this.#thumbs.length === 0) return;

    this.addEventListener('command', this.#handleCommand);

    this.#observer = new IntersectionObserver(this.#handleIntersect, {
      root: this.#track,
      threshold: 0.6,
    });
    Array.from(this.#track.children).forEach((slide) => this.#observer.observe(slide));
  }

  disconnectedCallback() {
    this.removeEventListener('command', this.#handleCommand);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #handleCommand = (event) => {
    if (event.command !== '--show-slide') return;
    const index = Number(event.source?.dataset.thumbIndex);
    const slide = this.#track.children[index];
    if (!slide) return;
    this.#track.scrollTo({
      left: index * this.#track.clientWidth * (getComputedStyle(this.#track).direction === 'rtl' ? -1 : 1),
      behavior: this.#reducedMotion.matches ? 'auto' : 'smooth',
    });
    this.#setCurrent(index);
  };

  #handleIntersect = (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const index = Array.prototype.indexOf.call(this.#track.children, entry.target);
      this.#setCurrent(index);
    });
  };

  #setCurrent(index) {
    this.#thumbs.forEach((thumb, i) => {
      if (i === index) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('s-gallery-thumbs')) {
  customElements.define('s-gallery-thumbs', GalleryThumbs);
}
